import { getDisplayName, type PatientInfo, type StudyInfo, type VolumeInfo } from '@/src/store/datasets-dicom';
import { useAppConfigStore } from '@/src/store/app-config';

function isAnonymized() {
  return useAppConfigStore().anonymize;
}

function cleanName(name: string) {
  return name.replace(/\s+/g, ' ').replace(/\^/g, ' ').trim();
}

function formatStudyDate(raw: string) {
  if (raw.length === 8) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw;
}

export function getVolumeDisplayName(info: VolumeInfo) {
  if (isAnonymized()) {
    const num = info.SeriesNumber?.trim();
    return num ? `Series ${num}` : 'Series';
  }
  return getDisplayName(info);
}

export function getPatientName(info: PatientInfo | undefined) {
  if (!info || isAnonymized()) return '';
  return cleanName(info.PatientName);
}

export function getPatientMeta(info: PatientInfo | undefined) {
  if (!info || isAnonymized()) return '';
  const parts: string[] = [];
  if (info.PatientSex) parts.push(info.PatientSex);
  if (info.PatientBirthDate) parts.push(`DOB ${info.PatientBirthDate}`);
  return parts.join(' · ');
}

export function getStudyDate(study: StudyInfo | undefined) {
  if (!study?.StudyDate || isAnonymized()) return '';
  return formatStudyDate(study.StudyDate);
}

export function getStatusBarPatientLine(options: {
  patient?: PatientInfo;
  study?: StudyInfo;
  modality?: string;
}) {
  const { patient, study, modality } = options;
  if (isAnonymized()) {
    return modality?.trim() ?? '';
  }
  const parts: string[] = [];
  const name = getPatientName(patient);
  if (name) parts.push(name);
  if (patient?.PatientSex) parts.push(patient.PatientSex);
  const date = getStudyDate(study);
  if (date) parts.push(date);
  if (modality) parts.push(modality);
  return parts.join(' · ');
}

export function getStudyTitle(study: StudyInfo) {
  if (isAnonymized()) {
    return 'Study';
  }
  return (
    study.StudyDescription?.trim() ||
    (study.StudyDate ? formatStudyDate(study.StudyDate) : '') ||
    study.StudyInstanceUID
  );
}

export function getStudySubtitle(study: StudyInfo) {
  if (isAnonymized() || !study.StudyDescription) return '';
  return study.StudyDate ? formatStudyDate(study.StudyDate) : '';
}

export function getPatientPanelLabel(name?: string) {
  if (isAnonymized()) return 'Studies';
  return name?.trim() || 'Studies';
}

export function getDocumentTitlePrefix(name?: string) {
  if (isAnonymized() || !name?.trim()) return '';
  return `${name.trim()} - `;
}

export function shouldShowDicomQuickInfo() {
  return !isAnonymized();
}

export function getVolumeTooltip(info: VolumeInfo) {
  if (isAnonymized()) return getVolumeDisplayName(info);
  return info.SeriesDescription?.trim() || getVolumeDisplayName(info);
}
